/**
 * Cryptographic utilities for MerriTrac
 *
 * Implements PBKDF2-HMAC-SHA1 password verification to maintain
 * compatibility with ASP.NET Rfc2898DeriveBytes implementation.
 *
 * CRITICAL: This must match the exact algorithm used in the legacy
 * BasicAuthHttpModule.cs to ensure existing passwords continue to work.
 */

import { sha1 } from '@noble/hashes/legacy';
import { pbkdf2 } from '@noble/hashes/pbkdf2';

/**
 * Verify a password against a stored PBKDF2 hash
 *
 * Algorithm matches ASP.NET Rfc2898DeriveBytes:
 * - PBKDF2-HMAC-SHA1
 * - 10,000 iterations
 * - 32-byte key length
 * - Base64-encoded salt and hash
 *
 * @param password - Plain text password to verify
 * @param saltBase64 - Base64-encoded salt from database
 * @param hashedPasswordBase64 - Base64-encoded hash from database
 * @returns true if password matches, false otherwise
 */
export function verifyPassword(
  password: string,
  saltBase64: string,
  hashedPasswordBase64: string,
): boolean {
  try {
    // Decode Base64 salt to Uint8Array
    const salt = base64ToUint8Array(saltBase64);

    // Derive key using PBKDF2-HMAC-SHA1 (matches ASP.NET)
    const derivedKey = pbkdf2(sha1, password, salt, {
      c: 10000, // Iteration count (matches Rfc2898DeriveBytes default)
      dkLen: 32, // Key length in bytes (matches Rfc2898DeriveBytes)
    });

    // Convert derived key to Base64 and compare
    const derivedKeyBase64 = uint8ArrayToBase64(derivedKey);

    // Constant-time comparison to prevent timing attacks
    return timingSafeEqual(derivedKeyBase64, hashedPasswordBase64);
  } catch (error: unknown) {
    console.error('Password verification failed:', error);
    return false;
  }
}

/**
 * Generate a new password hash (for user creation/password reset)
 *
 * @param password - Plain text password
 * @param saltBase64 - Optional Base64-encoded salt (generates random if not provided)
 * @returns Object with saltBase64 and hashedPasswordBase64
 */
export function hashPassword(
  password: string,
  saltBase64?: string,
): {
  saltBase64: string;
  hashedPasswordBase64: string;
} {
  // Generate random salt if not provided
  const salt =
    saltBase64 !== undefined && saltBase64 !== ''
      ? base64ToUint8Array(saltBase64)
      : crypto.getRandomValues(new Uint8Array(32));

  // Derive key using PBKDF2-HMAC-SHA1
  const derivedKey = pbkdf2(sha1, password, salt, {
    c: 10000,
    dkLen: 32,
  });

  return {
    saltBase64: uint8ArrayToBase64(salt),
    hashedPasswordBase64: uint8ArrayToBase64(derivedKey),
  };
}

/**
 * Parse HTTP Basic Auth header
 *
 * @param authHeader - Authorization header value (e.g., "Basic dXNlcjpwYXNz")
 * @returns Object with username and password, or null if invalid
 */
export function parseBasicAuth(authHeader: string | null): {
  username: string;
  password: string;
} | null {
  if (authHeader === null || authHeader === '' || !authHeader.startsWith('Basic ')) {
    return null;
  }

  try {
    // Extract and decode Base64 credentials
    const base64Credentials = authHeader.slice(6); // Remove "Basic "
    const credentials = atob(base64Credentials);

    // Split on first colon (password may contain colons)
    const colonIndex = credentials.indexOf(':');
    if (colonIndex === -1) {
      return null;
    }

    const username = credentials.slice(0, colonIndex);
    const password = credentials.slice(colonIndex + 1);

    return { username, password };
  } catch (error: unknown) {
    console.error('Failed to parse Basic Auth header:', error);
    return null;
  }
}

/**
 * Convert Base64 string to Uint8Array
 */
function base64ToUint8Array(base64: string): Uint8Array {
  // Remove padding if present
  const cleanBase64 = base64.replace(/=/g, '');

  // Decode using atob (available in Workers runtime)
  const binaryString = atob(cleanBase64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes;
}

/**
 * Convert Uint8Array to Base64 string
 */
function uint8ArrayToBase64(bytes: Uint8Array): string {
  // Convert to binary string
  let binaryString = '';
  for (const byte of bytes) {
    binaryString += String.fromCharCode(byte);
  }

  // Encode using btoa (available in Workers runtime)
  return btoa(binaryString);
}

/**
 * Constant-time string comparison to prevent timing attacks
 *
 * @param a - First string
 * @param b - Second string
 * @returns true if strings are equal, false otherwise
 */
function timingSafeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) {
    return false;
  }

  let result = 0;
  for (let i = 0; i < a.length; i++) {
    const charCodeA = a.charCodeAt(i);
    const charCodeB = b.charCodeAt(i);
    result |= charCodeA ^ charCodeB;
  }

  return result === 0;
}
